import { useState } from 'react'
import { X, Link2, FileText, Check } from 'lucide-react'
import { nanoid } from 'nanoid'
import { useAppStore } from '../store'
import { VaultItem } from '../config'
import { Modal, useModalClose } from './ui/Modal'
import { gradientFor } from '../lib/folderColors'

interface ItemEditorProps {
  item: VaultItem | null
  type: 'bookmark' | 'note'
  onClose: () => void
}

export function ItemEditor(props: ItemEditorProps) {
  const isEditing = !!props.item
  const noun = props.type === 'bookmark' ? 'link' : 'note'
  return (
    <Modal
      onClose={props.onClose}
      label={`${isEditing ? 'Edit' : 'New'} ${noun}`}
      glow={gradientFor(props.item?.folderId ?? null)[0]}
      maxWidth={620}
    >
      <EditorBody {...props} />
    </Modal>
  )
}

function EditorBody({ item, type }: ItemEditorProps) {
  const close = useModalClose()
  const vault = useAppStore((s) => s.unlockedVault)
  const selectedFolderId = useAppStore((s) => s.selectedFolderId)
  const updateVaultData = useAppStore((s) => s.updateVaultData)

  const [title, setTitle] = useState(item?.title || '')
  const [content, setContent] = useState(item?.content || '')
  const [description, setDescription] = useState(item?.description || '')
  const [folderId, setFolderId] = useState<string | null>(
    item?.folderId ?? selectedFolderId,
  )
  const [tagIds, setTagIds] = useState<string[]>(item?.tagIds || [])

  if (!vault) return null

  const isEditing = !!item
  const isBookmark = type === 'bookmark'
  const canSave = !!title.trim() && !!content.trim()

  const handleSave = () => {
    if (!canSave) return
    const now = new Date().toISOString()

    if (isEditing && item) {
      updateVaultData((data) => ({
        ...data,
        items: data.items.map((i) =>
          i.id === item.id
            ? {
                ...i,
                title: title.trim(),
                content: content.trim(),
                description: description.trim() || undefined,
                folderId,
                tagIds,
                updatedAt: now,
              }
            : i,
        ),
      }))
    } else {
      const newItem: VaultItem = {
        id: nanoid(),
        type,
        title: title.trim(),
        content: content.trim(),
        description: description.trim() || undefined,
        folderId,
        tagIds,
        createdAt: now,
        updatedAt: now,
      }
      updateVaultData((data) => ({ ...data, items: [...data.items, newItem] }))
    }
    close()
  }

  const toggleTag = (tagId: string) =>
    setTagIds((ids) =>
      ids.includes(tagId) ? ids.filter((id) => id !== tagId) : [...ids, tagId],
    )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSave()
      }}
    >
      <div className="mb-5 flex items-center gap-3">
        <span
          className={`kind-ic ${isBookmark ? 'bm' : 'note'}`}
          aria-hidden="true"
        >
          {isBookmark ? (
            <Link2 className="h-[18px] w-[18px]" />
          ) : (
            <FileText className="h-[18px] w-[18px]" />
          )}
        </span>
        <h2 className="flex-1 font-display text-xl font-semibold tracking-tight">
          {isEditing ? 'Edit' : 'New'} {isBookmark ? 'link' : 'note'}
        </h2>
        <button
          type="button"
          className="icon-btn"
          aria-label="Close"
          onClick={close}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="item-title">
            Title
          </label>
          <input
            id="item-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input"
            placeholder={isBookmark ? 'Website name' : 'Note title'}
            autoFocus
          />
        </div>

        <div>
          <label className="label" htmlFor="item-content">
            {isBookmark ? 'URL' : 'Content'}
          </label>
          {isBookmark ? (
            <input
              id="item-content"
              type="url"
              inputMode="url"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="input font-mono text-sm"
              placeholder="https://example.com"
            />
          ) : (
            <textarea
              id="item-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="input min-h-[200px] resize-y font-mono text-sm leading-relaxed"
              placeholder="Write your note in markdown…"
            />
          )}
        </div>

        <div>
          <label className="label" htmlFor="item-desc">
            Description{' '}
            <span className="font-normal text-faint">(optional)</span>
          </label>
          <textarea
            id="item-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input min-h-[64px] resize-y text-[15px]"
            placeholder="Add a short description"
          />
        </div>

        <div>
          <label className="label" htmlFor="item-folder">
            Folder
          </label>
          <select
            id="item-folder"
            value={folderId || ''}
            onChange={(e) => setFolderId(e.target.value || null)}
            className="input"
          >
            <option value="">No folder</option>
            {vault.data.folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </select>
        </div>

        <fieldset>
          <legend className="label">Tags</legend>
          <div className="flex flex-wrap gap-2">
            {vault.data.tags.map((tag) => {
              const on = tagIds.includes(tag.id)
              return (
                <button
                  key={tag.id}
                  type="button"
                  className="chip !min-h-[36px]"
                  aria-pressed={on}
                  onClick={() => toggleTag(tag.id)}
                >
                  {on ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <span className="dot" style={{ background: tag.color }} />
                  )}
                  {tag.name}
                </button>
              )
            })}
            {vault.data.tags.length === 0 && (
              <span className="text-sm text-faint">
                No tags yet — add them from the sidebar.
              </span>
            )}
          </div>
        </fieldset>
      </div>

      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" onClick={close} className="btn-secondary">
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSave}
          className="btn-primary sm:min-w-[140px]"
        >
          {isEditing ? 'Save changes' : isBookmark ? 'Add link' : 'Add note'}
        </button>
      </div>
    </form>
  )
}
